import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { UseFormSetValue, FieldValues } from 'react-hook-form';
import { getErrorMessage } from '@common/utils';
import {
  Checkpoint,
  InterviewQuestion,
  InterviewQuestions,
  InterviewQuestionsChildren,
  ProjectProps,
} from '@/types';
import {
  isGroup,
  isQuestion,
  createEmptyGroup,
  flattenGroupToQuestions,
  getDefaultGroupName,
  getDefaultSubgroupName,
} from '@/utils';
import { isCheckpoint } from '@/utils/questionGroupHelpers';

interface UseDiscussionGuideHandlersProps {
  groupStructure: InterviewQuestions;
  singleProjectData?: ProjectProps;
  setGroupStructure: (structure: InterviewQuestions) => void;
  updateGroup: (path: number[], updates: Partial<InterviewQuestions>) => void;
  setIsFormChanged: (changed: boolean) => void;
  setInterviewQuestionsStructureChanged: (changed: boolean) => void;
  setValue: UseFormSetValue<FieldValues>;
  setOpenQuestionId: (id: string | null) => void;
}

export const useDiscussionGuideHandlers = ({
  groupStructure,
  singleProjectData,
  setGroupStructure,
  updateGroup,
  setIsFormChanged,
  setInterviewQuestionsStructureChanged,
  setValue,
  setOpenQuestionId,
}: UseDiscussionGuideHandlersProps) => {
  // Helper to update both state and form value
  const updateStructure = (newStructure: InterviewQuestions) => {
    setGroupStructure(newStructure);

    // Clean structure - remove any numeric keys that React Hook Form might have added
    // These come from flat array registration (interview_questions.0.question, etc.)
    const cleanedStructure = Object.keys(newStructure).reduce((acc, key) => {
      // Only keep non-numeric keys (id, name, description, randomize, children)
      if (Number.isNaN(Number(key))) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (acc as any)[key] = (newStructure as any)[key];
      }
      return acc;
    }, {} as InterviewQuestions);

    setValue('interview_questions', cleanedStructure, { shouldDirty: true });
    setIsFormChanged(true);
    setInterviewQuestionsStructureChanged(true);
  };

  // Helper to create a new blank question
  const createNewQuestion = (): InterviewQuestion => ({
    id: uuidv4(),
    question: '',
    follow_up_question: '0',
    media: undefined,
    answer_quantification: [
      {
        quantify_answer: false,
        value: '',
      },
    ],
  });

  // ==================== GROUP MANAGEMENT HANDLERS ====================

  const handleCreateGroup = () => {
    const existingGroups = groupStructure.children?.filter(isGroup);
    const existingGroupsCount = existingGroups?.length || 0;

    if (existingGroupsCount === 0) {
      // First time creating groups
      const existingQuestions = groupStructure.children?.filter(isQuestion);
      const hasQuestions = existingQuestions && existingQuestions.length > 0;
      // TELV1-421 — defense-in-depth: grouping is hidden for embeds, but if this
      // handler is ever reached with root checkpoints, preserve them at root
      // rather than dropping them (they are neither questions nor groups).
      const existingCheckpoints = groupStructure.children?.filter(isCheckpoint) ?? [];

      if (hasQuestions) {
        // Has questions: create 2 groups (one with questions, one empty)
        const group1 = createEmptyGroup(getDefaultGroupName(1));
        group1.children = existingQuestions;

        const group2 = createEmptyGroup(getDefaultGroupName(2));

        updateStructure({
          ...groupStructure,
          children: [...existingCheckpoints, group1, group2] as InterviewQuestionsChildren,
        });
      } else {
        // Empty: create 1 group
        const group1 = createEmptyGroup(getDefaultGroupName(1));

        updateStructure({
          ...groupStructure,
          children: [...existingCheckpoints, group1] as InterviewQuestionsChildren,
        });
      }
    } else {
      // Add a new group to existing groups
      const groupNumber = existingGroupsCount + 1;
      const newGroup = createEmptyGroup(getDefaultGroupName(groupNumber));
      updateStructure({
        ...groupStructure,
        children: [...groupStructure.children, newGroup] as InterviewQuestionsChildren,
      });
    }
  };

  const handleGroupNameChange = (groupIndex: number, name: string, groupPath?: number[]) => {
    const path = groupPath || [groupIndex];
    updateGroup(path, { name });
    setIsFormChanged(true);
  };

  const handleGroupRandomizeChange = (
    groupIndex: number,
    randomize: boolean,
    groupPath?: number[],
  ) => {
    const path = groupPath || [groupIndex];
    updateGroup(path, { randomize });
    setIsFormChanged(true);
  };

  const handleGlobalRandomizeToggle = (enabled: boolean) => {
    updateStructure({
      ...groupStructure,
      randomize: enabled,
    });
  };

  const handleGroupDelete = (groupIndex: number, groupPath?: number[]) => {
    const path = groupPath || [groupIndex];

    const removeGroupFromStructure = (
      structure: InterviewQuestions,
      targetPath: number[],
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length - 1) {
        const idx = targetPath[depth];
        return {
          ...structure,
          children: structure.children.filter((_, i) => i !== idx) as InterviewQuestionsChildren,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return removeGroupFromStructure(child, targetPath, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const updatedStructure = removeGroupFromStructure(groupStructure, path);

    // Update local state only - changes will be saved when user clicks "Save Changes"
    updateStructure(updatedStructure);
  };

  // ==================== ADD HANDLERS ====================

  const handleAddQuestionToGroup = (groupIndex: number, groupPath?: number[]) => {
    const newQuestion = createNewQuestion();

    const updateStructureWithQuestion = (
      structure: InterviewQuestions,
      targetPath: number[],
      questionToAdd: InterviewQuestion,
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        const hasQuestions = structure.children.some(isQuestion);
        const hasSubgroups = structure.children.some(isGroup);

        if (!hasSubgroups) {
          return {
            ...structure,
            children: [...structure.children, questionToAdd] as InterviewQuestionsChildren,
          };
        }

        if (hasSubgroups && !hasQuestions) {
          const existingSubgroups = structure.children.filter(isGroup);
          const newSubgroupNumber = existingSubgroups.length + 1;
          const newSubgroup = createEmptyGroup(getDefaultSubgroupName(newSubgroupNumber));
          newSubgroup.children = [questionToAdd];

          return {
            ...structure,
            children: [...structure.children, newSubgroup] as InterviewQuestionsChildren,
          };
        }

        return {
          ...structure,
          children: [...structure.children, questionToAdd] as InterviewQuestionsChildren,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return updateStructureWithQuestion(child, targetPath, questionToAdd, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const path = groupPath || [groupIndex];
    const updatedStructure = updateStructureWithQuestion(groupStructure, path, newQuestion);
    updateStructure(updatedStructure);
    return newQuestion.id;
  };

  const handleAddQuestionWithOpen = (groupIndex: number, groupPath?: number[]) => {
    const questionId = handleAddQuestionToGroup(groupIndex, groupPath);
    if (questionId) setOpenQuestionId(questionId);
  };

  const handleAddSubgroupToGroup = (groupIndex: number, groupPath?: number[]): string | null => {
    const path = groupPath || [groupIndex];

    const navigateToGroup = (
      structure: InterviewQuestions,
      targetPath: number[],
    ): InterviewQuestions | null => {
      let current = structure;

      for (let i = 0; i < targetPath.length; i += 1) {
        const idx = targetPath[i];
        const child = current.children[idx];
        if (isGroup(child)) {
          current = child;
        } else {
          return null;
        }
      }

      return current;
    };

    const updateStructureWithSubgroups = (
      structure: InterviewQuestions,
      targetPath: number[],
      subgroups: InterviewQuestions[],
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        return {
          ...structure,
          children: subgroups,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return updateStructureWithSubgroups(child, targetPath, subgroups, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const targetGroup = navigateToGroup(groupStructure, path);

    if (!targetGroup) {
      return null;
    }

    const existingSubgroups = targetGroup.children.filter(isGroup);
    const existingQuestions = targetGroup.children.filter(isQuestion);

    if (existingSubgroups.length === 0 && existingQuestions.length > 0) {
      // First time creating subgroups - will create 2 subgroups
      const subgroup1 = createEmptyGroup(getDefaultSubgroupName(1));
      subgroup1.children = existingQuestions;

      const subgroup2 = createEmptyGroup(getDefaultSubgroupName(2));

      // Add a question to the second subgroup
      const newQuestion = createNewQuestion();
      subgroup2.children = [newQuestion];

      const updatedStructure = updateStructureWithSubgroups(groupStructure, path, [
        subgroup1,
        subgroup2,
      ]);
      updateStructure(updatedStructure);
      return newQuestion.id; // Return the question ID to open it
    }
    const subgroupNumber = existingSubgroups.length + 1;
    const newSubgroup = createEmptyGroup(getDefaultSubgroupName(subgroupNumber));

    // Add a question to the new subgroup
    const newQuestion = createNewQuestion();
    newSubgroup.children = [newQuestion];

    const updateStructureWithSubgroup = (
      structure: InterviewQuestions,
      targetPath: number[],
      subgroup: InterviewQuestions,
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        return {
          ...structure,
          children: [...structure.children, subgroup] as InterviewQuestionsChildren,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return updateStructureWithSubgroup(child, targetPath, subgroup, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const updatedStructure = updateStructureWithSubgroup(groupStructure, path, newSubgroup);
    updateStructure(updatedStructure);
    return newQuestion.id; // Return the question ID to open it
  };

  // ==================== DELETE HANDLERS ====================

  const removeQuestionFromStructure = (
    structure: InterviewQuestions,
    questionId: string,
  ): InterviewQuestions => {
    return {
      ...structure,
      children: structure.children
        .filter((child) => !(isQuestion(child) && child.id === questionId))
        .map((child) =>
          isGroup(child) ? removeQuestionFromStructure(child, questionId) : child,
        ) as InterviewQuestionsChildren,
    };
  };

  // #25 — checkpoints (embed-only, top-level) are only valid BETWEEN two
  // non-checkpoint nodes. Deleting a question can strand a checkpoint at the
  // start/end or leave two adjacent, which blocks Save until the user hunts it
  // down. Prune those orphans on delete: drop leading + adjacent checkpoints,
  // then trailing ones. No-op for non-embed guides (no checkpoints).
  const pruneOrphanedCheckpoints = (structure: InterviewQuestions): InterviewQuestions => {
    // Checkpoints live only at the root level, whose children are
    // (InterviewQuestion | Checkpoint)[].
    const children = structure.children as (InterviewQuestion | Checkpoint)[];
    const kept: (InterviewQuestion | Checkpoint)[] = [];
    children.forEach((child) => {
      const last = kept[kept.length - 1];
      if (isCheckpoint(child) && (!last || isCheckpoint(last))) return;
      kept.push(child);
    });
    while (kept.length && isCheckpoint(kept[kept.length - 1])) kept.pop();
    return { ...structure, children: kept as InterviewQuestionsChildren };
  };

  const handleDeleteQuestion = (
    idToDelete: string,
    setQuestionToDelete: (id: string | null) => void,
    setIsDeleteModalOpen: (open: boolean) => void,
  ) => {
    // Flatten the group structure to check if question exists in DB
    const flatQuestions = singleProjectData?.interview_questions
      ? flattenGroupToQuestions(singleProjectData.interview_questions)
      : [];
    const questionExistsInDB = flatQuestions.some(({ id }: InterviewQuestion) => id === idToDelete);

    if (!questionExistsInDB) {
      // Question doesn't exist in DB, delete immediately
      const updatedStructure = pruneOrphanedCheckpoints(
        removeQuestionFromStructure(groupStructure, idToDelete),
      );
      updateStructure(updatedStructure);
      return;
    }

    // Question exists in DB, show confirmation modal
    setQuestionToDelete(idToDelete);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteQuestion = async (
    questionToDelete: string | null,
    setIsDeletingQuestion: (deleting: boolean) => void,
    setIsDeleteModalOpen: (open: boolean) => void,
    setQuestionToDelete: (id: string | null) => void,
  ) => {
    if (!questionToDelete) return;

    setIsDeletingQuestion(true);

    try {
      // Remove the question from the tree structure (local state only)
      const updatedStructure = pruneOrphanedCheckpoints(
        removeQuestionFromStructure(groupStructure, questionToDelete),
      );

      updateStructure(updatedStructure);
      setIsDeleteModalOpen(false);
      setQuestionToDelete(null);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsDeletingQuestion(false);
    }
  };

  // ==================== CHECKPOINT HANDLERS (EMBED-ONLY) ====================

  const handleAddCheckpoint = () => {
    const newCheckpoint: Checkpoint = {
      id: uuidv4(),
      type: 'checkpoint',
      label: '',
    };
    updateStructure({
      ...groupStructure,
      children: [...groupStructure.children, newCheckpoint] as InterviewQuestionsChildren,
    });
  };

  const handleCheckpointLabelChange = (checkpointId: string, label: string) => {
    updateStructure({
      ...groupStructure,
      children: groupStructure.children.map((child) => {
        if (isCheckpoint(child) && child.id === checkpointId) {
          return { ...child, label } as Checkpoint;
        }
        return child;
      }) as InterviewQuestionsChildren,
    });
  };

  const handleDeleteCheckpoint = (checkpointId: string) => {
    updateStructure({
      ...groupStructure,
      children: groupStructure.children.filter(
        (child) => !(isCheckpoint(child) && child.id === checkpointId),
      ) as InterviewQuestionsChildren,
    });
  };

  return {
    handleCreateGroup,
    handleGroupNameChange,
    handleGroupRandomizeChange,
    handleGlobalRandomizeToggle,
    handleGroupDelete,
    handleAddQuestionWithOpen,
    handleAddSubgroupToGroup,
    handleDeleteQuestion,
    confirmDeleteQuestion,
    handleAddCheckpoint,
    handleCheckpointLabelChange,
    handleDeleteCheckpoint,
  };
};
